import { Stack } from '@mui/material';
import PropTypes from 'prop-types';
import {
  audienceAnalysisConfig,
  audienceAnalysisDownloadReportColumns,
} from '../../data/audienceAnalysisData';
import { formatNumberUsingUnit } from '../../utils/formatUtils';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export const HOUSEHOLD_COMPONENT = 'household_data';
export const AUDIENCE_ANALYSIS_COMPONENT = 'audience_analysis_data';

export const comparisonFormatter = (value, keyValue, showComparison) => {
  const percentValue = (keyValue?.currentValue / keyValue?.compareValue) * 100;

  const renderComparison = () => {
    if (!showComparison) {
      return null;
    }

    if (percentValue > 100) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', color: 'green' }}>
          (<ArrowUpwardIcon style={{ color: 'green', width: 15, height: 20 }} />
          <span style={{ color: 'green', fontSize: 10 }}>{percentValue.toFixed(2)}%</span>)
        </div>
      );
    }

    if (percentValue < 100) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', color: 'red' }}>
          (<ArrowDownwardIcon style={{ color: 'red', width: 15, height: 20 }} />
          <span style={{ color: 'red', fontSize: 10 }}>{percentValue?.toFixed(2)}%</span>)
        </div>
      );
    }

    return null;
  };

  switch (true) {
    case percentValue === 0:
    // falls through
    case percentValue === Infinity:
    // falls through
    case percentValue > 100:
    // falls through
    case percentValue < 100:
      return (
        <Stack direction={'row'} spacing={1}>
          <span>{value}</span>
          {renderComparison()}
        </Stack>
      );
    default:
      return (
        <Stack direction={'row'} spacing={1}>
          <span>{value}</span>
        </Stack>
      );
  }
};

export const comparePercentFormatter = (comparePercentValue) => {
  const renderContent = () => {
    if (comparePercentValue > 100) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ArrowUpwardIcon style={{ color: 'green', width: 15, height: 20 }} />
          <span style={{ color: 'green' }}>{comparePercentValue.toFixed(2)}%</span>
        </div>
      );
    }

    if (comparePercentValue < 100) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ArrowDownwardIcon style={{ color: 'red', width: 15, height: 20 }} />
          <span style={{ color: 'red' }}>{comparePercentValue.toFixed(2)}%</span>
        </div>
      );
    }

    if (comparePercentValue === 0) {
      return `${comparePercentValue.toFixed(2)}%`;
    }

    return 'N/A';
  };

  return (
    <Stack direction={'row'} spacing={1}>
      {renderContent()}
    </Stack>
  );
};

export const Tiers = [
  {
    label: 'Tier A',
    value: 'tierA',
  },
  {
    label: 'Tier B',
    value: 'tierB',
  },
  {
    label: 'Tier C',
    value: 'tierC',
  },
  {
    label: 'Tier D',
    value: 'tierD',
  },
];

const createTierCell = (tierName) => {
  const TierCell = ({ row }) => {
    const tierData = row?.original?.[tierName];
    const hasData = tierData || tierData?.[tierName] === 0;
    return (
      <div>
        {hasData
          ? comparisonFormatter(tierData[tierName], tierData.keyValue, row.showIndicator)
          : 'N/A'}
      </div>
    );
  };

  TierCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        [tierName]: PropTypes.shape({
          [tierName]: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          keyValue: PropTypes.object,
        }),
      }),
      showIndicator: PropTypes.bool,
    }),
  };

  return TierCell;
};

const TierACell = createTierCell('tierA');
const TierBCell = createTierCell('tierB');
const TierCCell = createTierCell('tierC');
const TierDCell = createTierCell('tierD');

export const tiersData = (showIndicator) => [
  {
    Header: 'Tier A',
    accessor: 'tierA',
    Cell: (props) => <TierACell {...props} showIndicator={showIndicator} />,
  },
  {
    Header: 'Tier B',
    accessor: 'tierB',
    Cell: (props) => <TierBCell {...props} showIndicator={showIndicator} />,
  },
  {
    Header: 'Tier C',
    accessor: 'tierC',
    Cell: (props) => <TierCCell {...props} showIndicator={showIndicator} />,
  },
  {
    Header: 'Tier D',
    accessor: 'tierD',
    Cell: (props) => <TierDCell {...props} showIndicator={showIndicator} />,
  },
];

const formatCellForDownload = (value, unit) => {
  return value ? formatNumberUsingUnit(value, unit) : 'N/A';
};

const createCellValue = (fieldName, value, unit, downloadData, keyValue) => {
  if (downloadData) {
    return { [fieldName]: formatCellForDownload(value, unit) };
  }
  return {
    [fieldName]: value
      ? { [fieldName]: formatNumberUsingUnit(value?.currentValue, unit), keyValue }
      : 'N/A',
  };
};

const configureCellValues = (key, keyValue, unit, downloadData) => {
  const value = keyValue;
  const cellMappings = [
    { suffix: '_total', fieldName: 'total' },
    { suffix: '_tier_a', fieldName: 'tierA' },
    { suffix: '_tier_b', fieldName: 'tierB' },
    { suffix: '_tier_c', fieldName: 'tierC' },
    { suffix: '_tier_d', fieldName: 'tierD' },
    { suffix: '_non_add', fieldName: 'nonAddressable' },
    { suffix: '_nurture', fieldName: 'nurture' },
  ];

  for (const mapping of cellMappings) {
    if (key?.endsWith(mapping.suffix)) {
      return createCellValue(mapping.fieldName, value, unit, downloadData, keyValue);
    }
  }

  return {};
};

const configureCellValuesHouseholdAvailable = (key, keyValue, unit, downloadData) => {
  const value = keyValue;
  const cellMappings = [
    { prefix: 'household_available', fieldName: 'total' },
    { prefix: 'tier_a', fieldName: 'tierA' },
    { prefix: 'tier_b', fieldName: 'tierB' },
    { prefix: 'tier_c', fieldName: 'tierC' },
    { prefix: 'tier_d', fieldName: 'tierD' },
    { prefix: 'non_add', fieldName: 'nonAddressable' },
    { prefix: 'nurture', fieldName: 'nurture' },
  ];

  for (const mapping of cellMappings) {
    if (key.startsWith(mapping.prefix)) {
      return createCellValue(mapping.fieldName, value, unit, downloadData, keyValue);
    }
  }

  return {};
};

const createKeyValuePair = (data, dataCompare, key) => {
  const hasValidData = data?.[key] && dataCompare?.[key];

  if (!hasValidData) {
    return { currentValue: null, compareValue: null };
  }

  const isDecimal = data[key]?.toString().includes('.');

  if (isDecimal) {
    return {
      currentValue: data[key]?.toFixed(2),
      compareValue: dataCompare[key]?.toFixed(2),
    };
  }

  return {
    currentValue: data[key],
    compareValue: dataCompare[key],
  };
};

const createKeyValuePairForSubRows = (data, dataCompare, key) => {
  const hasValidData =
    data && data[key] !== undefined && dataCompare && dataCompare[key] !== undefined;

  if (!hasValidData) {
    return { currentValue: null, compareValue: null };
  }

  const isDecimal = data[key]?.toString()?.includes('.');

  if (isDecimal) {
    return {
      currentValue: data[key]?.toFixed(2),
      compareValue: dataCompare[key]?.toFixed(2),
    };
  }

  return {
    currentValue: data[key],
    compareValue: dataCompare[key],
  };
};

const formatValueForDownload = (value) => {
  if (!value) {
    return value;
  }

  const isDecimal = value.toString().includes('.');
  return isDecimal ? value.toFixed(2) : value;
};

const calculateComparePercent = (totalCurrent, totalCompare) => {
  if (totalCurrent && totalCompare) {
    return ((totalCurrent / totalCompare) * 100).toFixed(2).toString().concat('%');
  }
  return 'N/A';
};

const buildDataArray = (data, condition) => {
  let dataArray = {};
  for (const key in data) {
    if (condition(key)) {
      dataArray = { ...dataArray, [key]: data[key] };
    }
  }
  return dataArray;
};

const configureRowValues = (dataArray, item, data, dataCompare) => {
  let obj = {};
  for (const key in dataArray) {
    let keyValue = createKeyValuePair(data, dataCompare, key);
    let unit = item.unit === 'none' ? '' : item.unit;
    let cell =
      item.value === 'household_available'
        ? configureCellValuesHouseholdAvailable(key, keyValue, unit)
        : configureCellValues(key, keyValue, unit);
    obj = { ...obj, ...cell };
  }
  return obj;
};
const getCompareData = (item, audienceAnalysisCompare, audienceAnalysisCurrent) => {
  let obj = {};
  let totalCompare;
  let totalCurrent;
  for (const key in audienceAnalysisCompare) {
    if (key.endsWith('_total')) {
      obj = { ...obj, [key]: audienceAnalysisCompare[key] };
    }
  }
  for (const key in obj) {
    if (key.startsWith(item.value)) {
      totalCompare = obj[key];
      totalCurrent = audienceAnalysisCurrent[key];
    }
  }
  return { totalCurrent, totalCompare };
};

const configureSubRows = (item, data, dataCompare) => {
  let subRows = item?.subRows;
  let subRowsValues = subRows.map((row) =>
    buildDataArray(data, (key) => key.startsWith(row.value))
  );

  let subRowsArray = subRowsValues.map((row, index) => {
    let obj = {};
    for (const key in row) {
      let keyValue = createKeyValuePairForSubRows(data, dataCompare, key);
      let unit = item.unit === 'none' ? '' : item.unit;
      let cell = configureCellValues(key, keyValue, unit);
      let compareTotal = getCompareData(subRows[index], dataCompare, data);
      let comparePercent = calculateComparePercent(
        compareTotal?.totalCurrent,
        compareTotal?.totalCompare
      );
      obj = {
        ...obj,
        propertyName: 'Measured Results',
        channels: subRows[index].label,
        ...cell,
        comparePercent,
        isSubrow: true,
      };
    }
    return obj;
  });
  return subRowsArray;
};

const processHouseholdRow = (item, data, dataCompare) => {
  const dataArray = buildDataArray(data, (key) => key.endsWith('household_available'));
  const comparePercent = calculateComparePercent(
    data?.household_available,
    dataCompare?.household_available
  );
  return { ...configureRowValues(dataArray, item, data, dataCompare), comparePercent };
};

const processRowWithSubRows = (item, data, dataCompare) => {
  const dataArray = buildDataArray(data, (key) => key.startsWith(item.value));
  const subRowsArray = configureSubRows(item, data, dataCompare);
  const compareTotal = getCompareData(item, dataCompare, data);
  const comparePercent = calculateComparePercent(
    compareTotal?.totalCurrent,
    compareTotal?.totalCompare
  );
  return {
    ...configureRowValues(dataArray, item, data, dataCompare),
    expand: true,
    subRows: subRowsArray,
    comparePercent,
  };
};

const processSimpleRow = (item, data, dataCompare) => {
  const dataArray = buildDataArray(data, (key) => key.startsWith(item.value));
  const compareTotal = getCompareData(item, dataCompare, data);
  const comparePercent = calculateComparePercent(
    compareTotal?.totalCurrent,
    compareTotal?.totalCompare
  );
  return { ...configureRowValues(dataArray, item, data, dataCompare), comparePercent };
};

const configureRow = (item, data, dataCompare) => {
  if (item.value === 'household_available') {
    return processHouseholdRow(item, data, dataCompare);
  }

  if (item?.subRows) {
    return processRowWithSubRows(item, data, dataCompare);
  }

  return processSimpleRow(item, data, dataCompare);
};

export const configureTableData = ({
  isDownload,
  options,
  storeId,
  audienceAnalysisCurrent,
  householdAvailableCurrent,
  audienceAnalysisCompare,
  householdAvailableCompare,
}) => {
  const result = (isDownload ? options?.map((store) => store?.value) : [storeId])?.map((store) => {
    const currentStoreHousehold = householdAvailableCurrent?.find(
      (data) => data?.store_id === store
    );
    const compareStoreHousehold = householdAvailableCompare?.find(
      (data) => data?.store_id === store
    );
    const currentAudienceAnalysis = audienceAnalysisCurrent?.find(
      (data) => data?.store_id === store
    );
    const compareAudienceAnalysis = audienceAnalysisCompare?.find(
      (data) => data.store_id === store
    );

    return {
      store_id: store,
      rows: [
        ...audienceAnalysisConfig.measuredResult.map((item) =>
          item.value === 'household_available'
            ? {
                propertyName: 'Measured Results',
                channels: item.label,
                compareData: compareStoreHousehold?.household_available,
                ...configureRow(item, currentStoreHousehold, compareStoreHousehold),
              }
            : {
                propertyName: 'Measured Results',
                channels: item.label,
                ...configureRow(item, currentAudienceAnalysis, compareAudienceAnalysis),
              }
        ),
        ...audienceAnalysisConfig.calculatedPerformanceIndicators.map((item) => ({
          propertyName: 'Calculated Performance Indicators',
          channels: item.label,
          ...configureRow(item, currentAudienceAnalysis, compareAudienceAnalysis),
        })),
      ],
    };
  });
  const updateResult = removeEmptyObjects(result);
  return [{ store_id: result[0]?.store_id, rows: updateResult }];
};

// download report configurations and functions-> the calulation is different from the table data hence separated

const configureDownloadRowValues = (dataArray, item, data, downloadData) => {
  let obj = {};
  for (const key in dataArray) {
    let keyValue = formatValueForDownload(data[key]);
    let unit = item.unit === 'none' ? '' : item.unit;
    let cell =
      item.value === 'household_available'
        ? configureCellValuesHouseholdAvailable(key, keyValue, unit, downloadData)
        : configureCellValues(key, keyValue, unit, downloadData);
    obj = { ...obj, ...cell };
  }
  return obj;
};

const configureDownloadSubRows = (item, data, dataCompare, downloadData) => {
  let subRows = item?.subRows;
  let subRowsValues = subRows.map((row) =>
    buildDataArray(data, (key) => key.startsWith(row.value))
  );

  let subRowsArray = subRowsValues.map((row, index) => {
    let obj = {};
    for (const key in row) {
      let keyValue = formatValueForDownload(data[key]);
      let unit = item.unit === 'none' ? '' : item.unit;
      let cell = configureCellValues(key, keyValue, unit, downloadData);
      let compareTotal = getCompareData(subRows[index], dataCompare, data);
      let comparePercent = calculateComparePercent(
        compareTotal?.totalCurrent,
        compareTotal?.totalCompare
      );
      obj = {
        ...obj,
        propertyName: 'Measured Results',
        channels: subRows[index].label,
        ...cell,
        comparePercent,
        isSubrow: true,
      };
    }
    return obj;
  });
  return subRowsArray;
};

const processHouseholdDownloadRow = (item, data, dataCompare, downloadData) => {
  const dataArray = buildDataArray(data, (key) => key.endsWith('household_available'));
  const comparePercent = calculateComparePercent(
    data?.household_available,
    dataCompare?.household_available
  );
  return { ...configureDownloadRowValues(dataArray, item, data, downloadData), comparePercent };
};

const processDownloadRowWithSubRows = (item, data, dataCompare, downloadData) => {
  const dataArray = buildDataArray(data, (key) => key.startsWith(item.value));
  const subRowsArray = configureDownloadSubRows(item, data, dataCompare, downloadData);
  const compareTotal = getCompareData(item, dataCompare, data);
  const comparePercent = calculateComparePercent(
    compareTotal?.totalCurrent,
    compareTotal?.totalCompare
  );
  return {
    ...configureDownloadRowValues(dataArray, item, data, downloadData),
    expand: true,
    subRows: subRowsArray,
    comparePercent,
  };
};

const processSimpleDownloadRow = (item, data, dataCompare, downloadData) => {
  const dataArray = buildDataArray(data, (key) => key.startsWith(item.value));
  const compareTotal = getCompareData(item, dataCompare, data);
  const comparePercent = calculateComparePercent(
    compareTotal?.totalCurrent,
    compareTotal?.totalCompare
  );
  return { ...configureDownloadRowValues(dataArray, item, data, downloadData), comparePercent };
};

const configureDownloadRow = (item, data, dataCompare, downloadData) => {
  if (item.value === 'household_available') {
    return processHouseholdDownloadRow(item, data, dataCompare, downloadData);
  }

  if (item?.subRows) {
    return processDownloadRowWithSubRows(item, data, dataCompare, downloadData);
  }

  return processSimpleDownloadRow(item, data, dataCompare, downloadData);
};

export const configureDownloadTableData = ({
  isDownload,
  options,
  storeId,
  audienceAnalysisCurrent,
  householdAvailableCurrent,
  audienceAnalysisCompare,
  householdAvailableCompare,
  downloadData,
}) => {
  const result = (isDownload ? options?.map((store) => store?.value) : [storeId])?.map((store) => {
    const currentStoreHousehold = householdAvailableCurrent?.find(
      (data) => data?.store_id === store
    );
    const compareStoreHousehold = householdAvailableCompare?.find(
      (data) => data?.store_id === store
    );
    const currentAudienceAnalysis = audienceAnalysisCurrent?.find(
      (data) => data?.store_id === store
    );
    const compareAudienceAnalysis = audienceAnalysisCompare?.find(
      (data) => data.store_id === store
    );

    return {
      store_id: store,
      rows: [
        ...audienceAnalysisConfig.measuredResult.map((item) =>
          item.value === 'household_available'
            ? {
                propertyName: 'Measured Results',
                channels: item.label,
                compareData: compareStoreHousehold?.household_available,
                ...configureDownloadRow(
                  item,
                  currentStoreHousehold,
                  compareStoreHousehold,
                  downloadData
                ),
              }
            : {
                propertyName: 'Measured Results',
                channels: item.label,
                ...configureDownloadRow(
                  item,
                  currentAudienceAnalysis,
                  compareAudienceAnalysis,
                  downloadData
                ),
              }
        ),
        ...audienceAnalysisConfig.calculatedPerformanceIndicators.map((item) => ({
          propertyName: 'Calculated Performance Indicators',
          channels: item.label,
          ...configureDownloadRow(
            item,
            currentAudienceAnalysis,
            compareAudienceAnalysis,
            downloadData
          ),
        })),
      ],
      columns: audienceAnalysisDownloadReportColumns?.filter(
        (col) => col?.accessor !== 'propertyName'
      ),
    };
  });
  return result;
};

const removeEmptyObjects = (result) => {
  return result[0]?.rows?.map((item) => {
    if (item?.expand) {
      return {
        ...item,
        subRows: item?.subRows?.filter((obj) => Object.keys(obj)?.length !== 0),
      };
    } else {
      return item;
    }
  });
};
